import React, { useEffect, useRef, useState } from "react";
import { Loader2, Target, Users, CheckCircle2, AlertTriangle, TrendingUp } from "lucide-react";
import { forecastPreview, driveForecast } from "@/lib/campusApi";

/* Drive forecast: eligible → likely to clear test → expected offers, with the
   rule that blocks the most students and the near-miss students one nudge away.
   Two modes: live preview while composing (eligibility + company) and a saved
   forecast for an existing drive (shows actuals as the drive progresses). */

const CONF = { high: "text-emerald-700", medium: "text-amber-700", low: "text-stone-500" };

export function ForecastPreview({ eligibility, companyName }) {
  const [fc, setFc] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const key = JSON.stringify([eligibility, companyName]);
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setBusy(true);
      try { const { data } = await forecastPreview(eligibility, companyName); setFc(data); } catch (_) { setFc(null); }
      finally { setBusy(false); }
    }, 500);
    return () => clearTimeout(timer.current);
  }, [key]); // eslint-disable-line
  return <ForecastBody fc={fc} busy={busy} compact />;
}

export function ForecastCard({ drive }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async (refresh = false) => {
    setBusy(true);
    try { const { data } = await driveForecast(drive.drive_id, refresh); setD(data); } catch (_) {}
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [drive.drive_id]); // eslint-disable-line
  if (!d) return <p className="mt-3 text-xs text-stone-400 inline-flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Forecasting…</p>;
  const fc = d.forecast, live = d.live, out = d.outcome;
  const closed = d.status === "closed";
  return (
    <div className="mt-4 rounded-2xl border border-stone-200 bg-stone-50 p-3" data-testid={`drive-forecast-${drive.drive_id}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono uppercase tracking-widest text-stone-500 inline-flex items-center gap-1"><Target size={11} /> Forecast</span>
        {!closed && <button onClick={() => load(true)} className="text-[10px] text-stone-400 hover:text-stone-900">{busy ? "…" : "recompute"}</button>}
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-center">
        <Mini label="eligible" v={fc.eligible} />
        <Mini label="clear test" v={fc.likely_clear_test} />
        <Mini label="offers" v={`~${fc.expected_offers}`} sub={fc.high ? `${fc.low} to ${fc.high}` : null} accent />
      </div>
      {(closed || live.applicants > 0) && (
        <p className="mt-2 text-xs text-stone-600">
          {closed ? "Actual" : "So far"}: {(out || live).applicants} applied · {(out || live).shortlisted} shortlisted · <b className={((out || live).offers >= fc.low && (out || live).offers <= fc.high) ? "text-emerald-700" : "text-stone-900"}>{(out || live).offers} offer{(out || live).offers === 1 ? "" : "s"}</b>
          {closed && <span className="text-stone-400"> vs ~{fc.expected_offers} forecast</span>}
        </p>
      )}
      {!closed && fc.near_miss?.length > 0 && <p className="mt-1.5 text-xs text-amber-700"><AlertTriangle size={11} className="inline mr-1" />{fc.near_miss.length} student{fc.near_miss.length > 1 ? "s" : ""} within {Math.max(...fc.near_miss.map((n) => n.gap))} points of eligibility.</p>}
    </div>
  );
}

function Mini({ label, v, sub, accent }) {
  return (
    <div className={`rounded-xl px-2 py-1.5 ${accent ? "bg-stone-900 text-stone-50" : "bg-white border border-stone-200"}`}>
      <p className="font-display text-xl leading-none">{v}</p>
      <p className={`text-[10px] mt-0.5 ${accent ? "text-stone-300" : "text-stone-500"}`}>{label}{sub ? ` · ${sub}` : ""}</p>
    </div>
  );
}

export function ForecastBody({ fc, busy, compact = false }) {
  if (!fc) return <div className="rounded-2xl border border-dashed border-stone-300 p-4 text-xs text-stone-500">{busy ? "Forecasting…" : "Set a company and eligibility to see a forecast."}</div>;
  const topBlock = Object.entries(fc.blockers || {}).sort((a, b) => b[1] - a[1])[0];
  return (
    <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4" data-testid="forecast-preview">
      <div className="flex items-center justify-between">
        <span className="overline inline-flex items-center gap-1.5"><Target size={12} /> Forecast {busy && <Loader2 size={11} className="animate-spin" />}</span>
        <span className={`text-[10px] font-mono uppercase ${CONF[fc.confidence] || CONF.low}`}>{fc.confidence} confidence</span>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <Stat icon={Users} label="Eligible" v={fc.eligible} sub={`of ${fc.pool}`} />
        <Stat icon={CheckCircle2} label="Likely to clear test" v={fc.likely_clear_test} sub={fc.no_test ? `${fc.no_test} untested` : "all tested"} />
        <Stat icon={TrendingUp} label="Offers expected" v={`~${fc.expected_offers}`} sub={fc.high ? `${fc.low} to ${fc.high}` : "none"} accent />
      </div>
      <p className="mt-3 text-sm text-stone-700 leading-relaxed">{fc.note}</p>
      {!compact && fc.by_branch?.length > 1 && (
        <div className="mt-3 flex flex-wrap gap-1.5">{fc.by_branch.map((b) => <span key={b.branch} className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-stone-200">{b.branch}: {b.eligible} eligible · ~{b.expected}</span>)}</div>
      )}
      {topBlock && topBlock[1] > 0 && fc.eligible < fc.pool && (
        <p className="mt-2 text-xs text-stone-500">Biggest blocker: the {topBlock[0].replace("_", " ")} rule excludes {topBlock[1]} student{topBlock[1] > 1 ? "s" : ""}.</p>
      )}
      {fc.near_miss?.length > 0 && (
        <p className="mt-1 text-xs text-amber-700"><AlertTriangle size={11} className="inline mr-1" />Near miss: {fc.near_miss.slice(0, 4).map((n) => `${n.name} (${n.score}, needs +${n.gap})`).join(", ")}{fc.near_miss.length > 4 ? ` and ${fc.near_miss.length - 4} more` : ""}.</p>
      )}
      {fc.strong?.length > 0 && !compact && (
        <p className="mt-2 text-xs text-stone-600">Strongest: {fc.strong.slice(0, 5).map((s) => `${s.name} (${s.score})`).join(", ")}.</p>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, v, sub, accent }) {
  return (
    <div className={`rounded-xl p-3 ${accent ? "bg-stone-900 text-stone-50" : "bg-white border border-stone-200"}`}>
      <Icon size={13} className={accent ? "text-amber-300" : "text-stone-400"} />
      <p className="font-display text-2xl mt-1 leading-none">{v}</p>
      <p className={`text-[10px] mt-1 ${accent ? "text-stone-300" : "text-stone-500"}`}>{label}{sub ? ` · ${sub}` : ""}</p>
    </div>
  );
}
